/**
 * Threat record schema — DataTable columns + FormModal fields for
 * Step 4 (`threat_analysis_agent`).
 *
 * Real DB shape:
 *   { threat_id, stride_category, asset_id, damage_ids: string[],
 *     compromised_property, threat_scenario, threat_source,
 *     // hierarchical (when source === 'child_tara'):
 *     source, source_session_id, is_relevant, relevance_justification, ... }
 *
 * Per ISO 21434, `compromised_property` is auto-derived from
 * `stride_category` (Spoofing→Authenticity, Tampering→Integrity, etc.).
 * The form modal hides the property field and the consumer derives it
 * on save (mirrors legacy Step4 logic).
 */
import React from 'react';
import CategoryPill from '../../../../components/ui/CategoryPill';

export interface ThreatRecord {
  threat_id: string;
  stride_category?: string;
  asset_id?: string;
  damage_ids?: string[];
  compromised_property?: string;
  threat_scenario?: string;
  threat_source?: string;
  /** Hierarchical mode (`source === 'child_tara'` flags an inherited row). */
  source?: string;
  source_session_id?: string;
  is_relevant?: boolean;
  relevance_justification?: string;
  [key: string]: unknown;
}

/** Canonical STRIDE category constants (ISO 21434 — uppercase with
 *  underscores in the data layer; title-cased display labels above). */
export const STRIDE_CATEGORIES = [
  { value: 'SPOOFING', label: 'Spoofing' },
  { value: 'TAMPERING', label: 'Tampering' },
  { value: 'REPUDIATION', label: 'Repudiation' },
  { value: 'INFORMATION_DISCLOSURE', label: 'Information Disclosure' },
  { value: 'DENIAL_OF_SERVICE', label: 'Denial of Service' },
  { value: 'ELEVATION_OF_PRIVILEGE', label: 'Elevation of Privilege' },
] as const;

export const STRIDE_VALUE_TO_LABEL: Record<string, string> = Object.fromEntries(
  STRIDE_CATEGORIES.map((c) => [c.value, c.label]),
);

/** ISO 21434: STRIDE category → compromised security property. */
const STRIDE_TO_PROPERTY: Record<string, string> = {
  SPOOFING: 'Authenticity',
  TAMPERING: 'Integrity',
  REPUDIATION: 'Non-repudiation',
  INFORMATION_DISCLOSURE: 'Confidentiality',
  DENIAL_OF_SERVICE: 'Availability',
  ELEVATION_OF_PRIVILEGE: 'Authorization',
};

export function derivePropertyFromStride(stride: string | undefined): string {
  return stride ? STRIDE_TO_PROPERTY[stride] || '' : '';
}

const joinList = (v: unknown): string =>
  Array.isArray(v) ? v.join(', ') : v == null ? '' : String(v);

/** STRIDE pill — outlined `CategoryPill` (text-primary border + text,
 *  transparent fill). Reads as a neutral category tag, separate from
 *  the impact/severity LevelBadge family. */
const renderStride = (row: ThreatRecord) =>
  row.stride_category ? (
    <CategoryPill
      label={STRIDE_VALUE_TO_LABEL[row.stride_category] ?? row.stride_category}
      size="sm"
    />
  ) : (
    ''
  );

/** Column widths sum to 100% in read mode; edit mode reserves 4% for
 *  the trailing `_actions` kebab. Convention shared with Steps 2-3. */
export const THREAT_COLUMNS_READ = [
  { key: 'threat_id', label: 'ID', sortable: true, width: '10%' },
  { key: 'stride_category', label: 'STRIDE', sortable: true, width: '14%', render: renderStride },
  { key: 'asset_id', label: 'Target Asset', sortable: true, width: '11%' },
  {
    key: 'damage_ids',
    label: 'Damage IDs',
    sortable: false,
    width: '10%',
    render: (row: ThreatRecord) => joinList(row.damage_ids),
  },
  { key: 'compromised_property', label: 'Property', sortable: true, width: '11%' },
  { key: 'threat_scenario', label: 'Scenario', sortable: false, width: '44%' },
];

export const THREAT_COLUMNS_EDIT = [
  { key: 'threat_id', label: 'ID', sortable: true, width: '9%' },
  { key: 'stride_category', label: 'STRIDE', sortable: true, width: '13%', render: renderStride },
  { key: 'asset_id', label: 'Target Asset', sortable: true, width: '11%' },
  {
    key: 'damage_ids',
    label: 'Damage IDs',
    sortable: false,
    width: '10%',
    render: (row: ThreatRecord) => joinList(row.damage_ids),
  },
  { key: 'compromised_property', label: 'Property', sortable: true, width: '11%' },
  { key: 'threat_scenario', label: 'Scenario', sortable: false, width: '42%' },
];

export const THREAT_ACTIONS_COLUMN_WIDTH = '4%';

const THREAT_ID_PATTERN = /^[A-Z0-9_-]+$/;

/** FormModal field config. Options for asset_id + damage_ids + threat_source
 *  are injected at render time by `ThreatFormModal` since they depend
 *  on session data + Step 2/3 records. `compromised_property` is
 *  derived from `stride_category` on save — not exposed to the user. */
export const THREAT_FORM_FIELDS = [
  {
    name: 'threat_id',
    label: 'Threat ID',
    type: 'text',
    required: true,
    autoFocus: true,
    placeholder: 'e.g. THREAT_001',
    helpText: 'Unique identifier — uppercase letters, numbers, underscores, hyphens.',
    validate: (v: unknown) => {
      const s = String(v ?? '').trim();
      if (!s) return 'Threat ID is required';
      if (!THREAT_ID_PATTERN.test(s)) {
        return 'Threat ID must contain only uppercase letters, numbers, underscores, and hyphens';
      }
      return null;
    },
    width: 'half',
  },
  {
    name: 'stride_category',
    label: 'STRIDE Category',
    type: 'select',
    required: true,
    options: STRIDE_CATEGORIES.map((c) => ({ value: c.value, label: c.label })),
    helpText: 'Auto-derives the compromised security property on save.',
    width: 'half',
  },
  {
    name: 'asset_id',
    label: 'Target Asset',
    type: 'select',
    required: true,
    // Options injected from Step 2 asset list.
    options: [] as Array<{ value: string; label: string }>,
    helpText: 'The asset this threat targets.',
  },
  {
    name: 'damage_ids',
    label: 'Damage Scenarios',
    type: 'multi-select',
    required: false,
    // Options injected from Step 3 damage-scenario list.
    options: [] as Array<{ value: string; label: string }>,
    helpText: 'Damage scenarios this threat enables (zero or more).',
    defaultValue: [],
  },
  {
    name: 'threat_scenario',
    label: 'Threat Scenario',
    type: 'textarea',
    required: true,
    rows: 5,
    placeholder: 'Describe how the attacker exploits this threat against the target asset.',
    helpText: 'Per ISO 21434: the attack narrative — actor, method, intermediate steps, and intent.',
    validate: (v: unknown) => {
      const s = String(v ?? '').trim();
      if (s.length < 30) {
        return 'Scenario must be at least 30 characters.';
      }
      return null;
    },
  },
  {
    name: 'threat_source',
    label: 'Threat Source',
    type: 'select',
    required: false,
    options: [
      { value: 'Custom', label: 'Custom' },
      { value: 'UN R155', label: 'UN R155' },
      { value: 'Database', label: 'Database' },
    ],
    defaultValue: 'Custom',
    helpText: 'Where the threat was sourced — manual entry or a reference catalog.',
    width: 'half',
  },
];
